import { createContext, PropsWithChildren, useContext, useLayoutEffect, useMemo, useState } from 'react';
import {
  DefaultAccountClient,
  type IdentityEnvironment,
} from '@atlassian/account-client';
import { useToken } from '../components/Flow/ConsentContextProvider';

const fedrampSuffix = '-fedm';
const rawEnv = process.env.ENV_TYPE ?? 'dev';
const isFedramp = rawEnv.endsWith(fedrampSuffix);
// If the env ends with "-fedm" then strip the suffix
const envType = isFedramp
  ? rawEnv.substring(0, rawEnv.length - fedrampSuffix.length)
  : rawEnv;

const AccountClientContext = createContext<DefaultAccountClient | null>(null);

type AccountClientProviderProps = PropsWithChildren<{ accountClient?: DefaultAccountClient }>;

export function AccountClientProvider({ children, accountClient }: AccountClientProviderProps) {
  const defaultClient = useMemo(
    () =>
      accountClient ??
      new DefaultAccountClient({
        identityEnvironment: envType as IdentityEnvironment,
        defaultContinueUrl: window.location.href,
        perimeter: isFedramp ? 'fedramp-moderate' : 'commercial',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return <AccountClientContext.Provider value={defaultClient}>{children}</AccountClientContext.Provider>;
}

export const useAccountClient = () => {
  const client = useContext(AccountClientContext);
  if (!client) throw new Error('useAccountClient must be used within AccountClientProvider');
  return client;
};

// Copied from the @atlassian/account-client docs
// Will be undefined if still loading, null if logged out, or the user's profile otherwise.
export function useUser() {
  const client = useAccountClient();
  const [user, setUser] = useState(client.currentUser);
  useLayoutEffect(() => client.addUserListener(setUser), []);
  if (user === null && process.env.APP_VERSION !== 'test') {
    console.warn('Not logged in, redirecting to login page');
    window.location.href = client.getLoginLink();
  }
  return user;
}

export function useSwitchAccountLink(prependIdDomainUrl?: boolean) {
  const client = useAccountClient();
  const { audience, redirectUrl } = useToken();
  const continueUrl = `https://${audience}${redirectUrl?.raw}`;
  if (prependIdDomainUrl ?? true) {
    return client.getSwitchAccountLink({ continueUrl });
  }
  return continueUrl;
}
